import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Radar, ArrowLeft, Building2, RefreshCw, CheckSquare, Square, AlertOctagon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FLAG_TABS = [
  { key: "ALL_FLAGGED", label: "All Flagged" },
  { key: "DORMANT_WATCH", label: "Watch · 30-59d" },
  { key: "DORMANT_60", label: "60+ days" },
  { key: "DORMANT_90", label: "90+ days" },
  { key: "GHOST_SUSPECT", label: "Ghost Suspect" },
];

const FLAG_STYLES = {
  DORMANT_WATCH: "bg-amber-100 text-amber-800 border-amber-200",
  DORMANT_60: "bg-orange-100 text-orange-800 border-orange-200",
  DORMANT_90: "bg-red-100 text-red-800 border-red-200",
  GHOST_SUSPECT: "bg-slate-900 text-white border-slate-900",
};

function BulkExitModal({ open, onClose, employees, onSubmitted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;
  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/dashboard/zero-attendance/bulk-exit", {
        employee_ids: employees.map(e => e.id),
        exit_type: "Absconding",
        resignation_date: today,
        requested_lwd: today,
        notice_period_days: 0,
        exit_reason_category: "Dormancy Radar — long-term ghost suspect",
        notes: "Auto-flagged by Dormancy Radar — no punches / no salary for 60+ days",
        rehire_eligibility: "No",
      });
      toast.success(`${data.created}/${data.requested} exit requests submitted for approval`);
      if (data.failed > 0) {
        toast.warning(`${data.failed} failed — check console`);
        console.warn("Bulk exit errors:", data.errors);
      }
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk exit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="dormancy-bulk-exit-modal">
      <div className="bg-white rounded-lg w-full max-w-xl shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-red-600" />
            <h2 className="font-bold text-lg text-slate-900" style={{ fontFamily: "Chivo" }}>
              Mark {employees.length} Ghost Suspects as Left
            </h2>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            Each employee will get a PENDING Absconding exit request (LWD = today).
            Super Admin must approve individually. Employees stay Working until approved.
          </div>
          <div className="max-h-48 overflow-auto bg-slate-50 rounded p-2 text-[11px] space-y-0.5 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
              {employees.length} selected employee(s):
            </div>
            {employees.map(e => (
              <div key={e.id} className="text-slate-700">
                <span className="font-mono text-slate-500">{e.punch_id}</span> · {e.name}
                {" "}<span className="text-slate-400">({e.dormancy_days_since_present}d dormant)</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="dormancy-bulk-cancel">
            Cancel
          </Button>
          <Button
            onClick={doSubmit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="dormancy-bulk-submit"
          >
            {submitting ? "Submitting…" : `Submit ${employees.length} for Approval`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardDormancy() {
  const [params, setParams] = useSearchParams();
  const flag = params.get("flag") || "ALL_FLAGGED";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      setSelected(new Set());
      try {
        const { data } = await api.get(`/dashboard/dormancy?flag=${flag}`);
        if (live) setData(data);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [flag, refreshTick]);

  const doRecompute = async () => {
    setRefreshing(true);
    try {
      await api.post("/dormancy/recompute");
      toast.success("Dormancy flags recomputed");
      setRefreshTick(t => t + 1);
    } catch {
      toast.error("Recompute failed");
    } finally {
      setRefreshing(false);
    }
  };

  const setFlag = (f) => {
    const next = new URLSearchParams(params);
    next.set("flag", f);
    setParams(next);
  };

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.punch_id || "").toLowerCase().includes(q) ||
      (r.employee_code || "").toLowerCase().includes(q) ||
      (r.department_name || "").toLowerCase().includes(q) ||
      (r.unit_name || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const canBulkExit = flag === "GHOST_SUSPECT" || flag === "DORMANT_90";
  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someSelected = filtered.some(r => selected.has(r.id));

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach(r => next.delete(r.id));
    else filtered.forEach(r => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectedRows = rows.filter(r => selected.has(r.id));

  return (
    <div className="space-y-5" data-testid="dashboard-dormancy-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Radar className="h-7 w-7 text-red-600" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HR Snapshot · Ghost Radar</div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              Dormancy Radar
            </h1>
          </div>
        </div>
        <button
          onClick={doRecompute}
          disabled={refreshing}
          className="text-xs uppercase tracking-wider text-slate-500 hover:text-red-600 flex items-center gap-2 border border-slate-200 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          data-testid="dormancy-page-recompute"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Computing…" : "Recompute"}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="dormancy-tabs">
        {FLAG_TABS.map(t => {
          const c = data?.counts_by_flag?.[t.key];
          const active = flag === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setFlag(t.key)}
              data-testid={`dormancy-tab-${t.key.toLowerCase()}`}
              className={`text-xs rounded-full px-3 py-1.5 border transition ${
                active
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-red-300"
              }`}
            >
              {t.label}
              {typeof c === "number" && t.key !== "ALL_FLAGGED" && (
                <span className={`ml-1.5 text-[10px] ${active ? "text-red-100" : "text-slate-400"}`}>
                  ({c})
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="dormancy-count-strip">
        <div className="flex items-center gap-3">
          <div className="text-3xl font-bold text-red-600" style={{ fontFamily: "Chivo" }}>
            {loading ? "…" : (data?.count ?? 0)}
          </div>
          <div className="text-sm text-slate-600 max-w-xl leading-snug">
            {flag === "GHOST_SUSPECT"
              ? "60+ days dormant AND missing / zero salary in the last 2 payroll runs. Highest confidence ghost employees."
              : flag === "DORMANT_90"
              ? "No attendance punch in 90 or more days."
              : flag === "DORMANT_60"
              ? "No attendance punch in 60-89 days."
              : flag === "DORMANT_WATCH"
              ? "No attendance punch in 30-59 days. Watch list."
              : "All employees flagged as dormant (30+ days without punch)."
            }
          </div>
        </div>
        <input
          data-testid="dormancy-filter"
          placeholder="Filter by name, punch id, dept, unit…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded px-3 py-2 w-64 focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      {!loading && (data?.count ?? 0) === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-sm text-slate-400">
          No employees in this dormancy bucket. 🎉
        </div>
      )}

      {!loading && (data?.count ?? 0) > 0 && (
        <>
          {someSelected && canBulkExit && (
            <div className="sticky top-2 z-10 bg-red-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg" data-testid="dormancy-bulk-bar">
              <div className="text-sm font-medium">
                {selectedRows.length} selected · will be routed through Super Admin approval
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs bg-white/10 hover:bg-white/20 rounded px-3 py-1.5"
                  data-testid="dormancy-clear-selection"
                >
                  Clear
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="text-xs bg-white text-red-600 font-semibold rounded px-3 py-1.5 hover:bg-red-50"
                  data-testid="dormancy-open-bulk-modal"
                >
                  Mark {selectedRows.length} as Left →
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  {canBulkExit && (
                    <th className="px-3 py-2 w-8">
                      <button onClick={toggleAll} className="text-slate-500 hover:text-red-600" data-testid="dormancy-toggle-all">
                        {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                    </th>
                  )}
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2">Punch / Code</th>
                  <th className="px-3 py-2">Flag</th>
                  <th className="px-3 py-2">Days Dormant</th>
                  <th className="px-3 py-2">Last Present</th>
                  <th className="px-3 py-2">Last Paid</th>
                  <th className="px-3 py-2">Department</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2">CTC</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isSel = selected.has(e.id);
                  const days = e.dormancy_days_since_present ?? 0;
                  return (
                    <tr
                      key={e.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-red-50" : ""}`}
                      data-testid={`dormancy-row-${e.id}`}
                    >
                      {canBulkExit && (
                        <td className="px-3 py-2">
                          <button
                            onClick={() => toggleOne(e.id)}
                            className="text-slate-500 hover:text-red-600"
                            data-testid={`dormancy-select-${e.id}`}
                          >
                            {isSel ? <CheckSquare className="h-4 w-4 text-red-600" /> : <Square className="h-4 w-4" />}
                          </button>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <Link to={`/employees/${e.id}`} className="font-semibold text-slate-900 hover:text-red-600">
                          {e.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        <div>{e.punch_id}</div>
                        <div className="text-slate-400">{e.employee_code}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`${FLAG_STYLES[e.dormancy_flag] || "bg-slate-100 text-slate-800"} text-[9px] border`}>
                          {e.dormancy_flag}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-sm font-bold ${
                          days >= 90 ? "text-red-600" : days >= 60 ? "text-orange-600" : "text-amber-600"
                        }`}>
                          {days >= 9999 ? "—" : `${days}d`}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {e.dormancy_last_present || <span className="text-slate-400">never</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {e.dormancy_last_salary_month
                          ? e.dormancy_last_salary_month.slice(0, 7)
                          : <span className="text-slate-400">none</span>}
                        {e.dormancy_payroll_zero && (
                          <div className="text-[9px] text-red-500">missing recent</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">{e.department_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-slate-400" />
                          {e.unit_name || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-sm text-slate-900">
                        ₹{(e.ctc || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-6 text-center text-xs text-slate-400">
                No employees match &quot;{filter}&quot;
              </div>
            )}
          </div>
        </>
      )}

      <BulkExitModal
        open={showModal}
        onClose={() => setShowModal(false)}
        employees={selectedRows}
        onSubmitted={() => {
          setShowModal(false);
          setSelected(new Set());
          setRefreshTick(t => t + 1);
        }}
      />
    </div>
  );
}
